import axios from '../lib/axios';
import { AxiosResponse, isAxiosError, isCancel } from 'axios';
import { API_BASE_URL } from '../config';

/**
 * Every admin endpoint in this module reports failures as `{"error": "..."}`
 * (see backend/los_app/admin_views.py). Extracted into shared helpers so each
 * panel stops re-deriving its own `(err as any).response.data.error` cast --
 * the same generalisation `lms_staff/lmsStaffApi.ts` already made for the LMS
 * endpoints, applied here to the los_app admin API.
 */
export interface AdminApiErrorBody {
    error?: string;
    detail?: string;
}

export function getAdminApiErrorMessage(error: unknown, fallback: string): string {
    if (isAxiosError<AdminApiErrorBody>(error)) {
        return error.response?.data?.error || error.response?.data?.detail || fallback;
    }
    return fallback;
}

/** HTTP status of an axios error, or `undefined` for a non-HTTP failure. */
export function getAdminApiErrorStatus(error: unknown): number | undefined {
    return isAxiosError(error) ? error.response?.status : undefined;
}

/**
 * True when the request never got an HTTP response at all (offline, DNS, CORS,
 * timeout). Distinguished from a 4xx/5xx because the remedy differs: a network
 * failure is worth retrying, a 403 never is.
 */
export function isAdminApiNetworkError(error: unknown): boolean {
    return isAxiosError(error) && !error.response;
}

/**
 * True for a request the caller itself aborted (superseded filter/page change).
 * MUST be checked before `isAdminApiNetworkError`, which an abort also
 * satisfies (an aborted request has no response either) — otherwise every
 * keystroke that supersedes an in-flight request would flash a bogus
 * "network error" state.
 */
export function isAdminApiCanceled(error: unknown): boolean {
    return isCancel(error);
}

// ---------------------------------------------------------------------------
// Expenses panel — Digitap API logs
// @origin: v2 | @since: 0570ba2d | @used-by: ALL
//
// Types written against the ACTUAL view source, not a summary:
//   backend/los_app/admin_views.py::admin_digitap_logs_list
//   backend/los_app/urls.py  ->  path('admin/digitap_logs/', ...)
//
// v2's adminApi.ts shipped this call effectively untyped (`params as any`, no
// response type) and its TSX hardcoded the api_type/outcome enum lists. Both
// are fixed here: the response is fully typed, and the filter vocabulary comes
// from the server-provided `choices` block instead of a duplicated literal.
// ---------------------------------------------------------------------------

/** One `{value,label}` pair out of the response's `choices` block. */
export interface DigitapChoice {
    value: string;
    label: string;
}

/**
 * A single DigitapAPILog row.
 *
 * `request_payload` / `response_raw` are OPTIONAL because the backend withholds
 * them unless `include_payloads=1` is passed -- unlike v2, which embedded both
 * jsonb blobs in every row of every page. They are `Record<string, unknown>`
 * rather than `any` so nothing can be dereferenced off them without a
 * deliberate narrowing; the panel only ever stringifies them.
 */
export interface DigitapLogRow {
    id: number;
    user_id: number | null;
    username: string | null;
    api_type: string;
    outcome: string;
    result_code: number | null;
    error_detail: string;
    created_at: string | null;
    request_payload?: Record<string, unknown> | null;
    response_raw?: Record<string, unknown> | null;
}

/**
 * Aggregates are computed over the FILTERED set before pagination, and every
 * known bucket is zero-filled server-side, so the key sets are stable for
 * rendering. `by_api_type_outcome` is a SPARSE matrix -- only non-zero cells
 * are present -- hence the nested lookup must tolerate missing keys.
 */
export interface DigitapLogsAggregates {
    total: number;
    by_api_type: Record<string, number>;
    by_outcome: Record<string, number>;
    by_api_type_outcome: Record<string, Record<string, number>>;
}

export interface DigitapLogsChoices {
    api_type: DigitapChoice[];
    outcome: DigitapChoice[];
}

export interface DigitapLogsResponse {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    count: number;
    results: DigitapLogRow[];
    include_payloads: boolean;
    filters: Record<string, string | number>;
    aggregates: DigitapLogsAggregates;
    choices: DigitapLogsChoices;
    max_page_size: number;
}

export interface DigitapLogsParams {
    page?: number;
    page_size?: number;
    api_type?: string;
    outcome?: string;
    user_id?: string;
    search?: string;
    created_from?: string;
    created_to?: string;
    /** Omit entirely for the default (payload-free) response. */
    include_payloads?: 1;
}

// ── Caprion Manage Mandates ──────────────────────────────────────────────────
//
// Mirrors backend/los_app/admin_views.py::admin_caprion_list_pending_mandates.
// One row per not-yet-verified mandate for the PhonePe BAP.

export interface CaprionPendingMandateRow {
    /** UserMandateRegistrationMapping.mandate_id — the Digio mandate id. Caprion
     *  calls the same value merchant_request_number. Used as the React key AND as
     *  the payload for caprionVerifyMandate below. */
    mandate_id: string;
    user_id: number | null;
    username: string | null;
    /** First+last name when set, else falls back to username. */
    user_display: string | null;
    /** Raw status code as stored ("1".."5"), or "" when never set. */
    status: string;
    /** Human label for `status`: waiting | processing | rejected | accepted |
     *  unknown | "not set". */
    status_label: string;
    /** Derived server-side from the row's auth_type — never client-supplied. */
    mandate_type: "upi" | "nach";
    umrn: string | null;
    created_at: string | null;
}

export interface CaprionPendingMandatesResponse {
    ok: boolean;
    /** The BAP this list is scoped to — always "PhonePe" for now. */
    bap: string;
    count: number;
    results: CaprionPendingMandateRow[];
}

// ── Caprion Journey Status ───────────────────────────────────────────────────
//
// Mirrors backend/los_app/admin_views.py::admin_caprion_journey_status. The
// column list is data-driven (`columns` in the response) because the number of
// `check_status_N` columns depends on how many times the busiest user was
// polled, so it cannot be hardcoded here.

/** One column descriptor; `is_json` marks a check_status_N response blob. */
export interface CaprionJourneyColumn {
    key: string;
    label: string;
    is_json?: boolean;
}

/** One persisted check_status call, as embedded in a `check_status_N` cell. */
export interface CaprionCheckStatusCall {
    id: number;
    status_type: string | null;
    lender_status: string | null;
    /** Caprion's echo of our reference, e.g. "INTL-151-3DB3884B". */
    order_id: string | null;
    success: boolean | null;
    status_code: number | null;
    is_final_response: boolean | null;
    created_at: string | null;
    /**
     * The verbatim Caprion response body.
     *
     * ABSENT in the list endpoint's payload — these bodies average ~2.7 KB and
     * shipping one per call per row is what made BRE Analysis slow. The table
     * needs none of it (a cell renders a status badge plus a "JSON" button), so
     * it is fetched for a single call via `caprionCheckStatusResponse(id)` when
     * the operator opens the viewer. Still present in the CSV export.
     */
    response?: unknown;
}

/** Response of `caprionGenerateInitialOffer`. */
export interface CaprionGenerateInitialOfferResponse {
    success: boolean;
    /** false when an offer already existed and nothing was generated. */
    created: boolean;
    detail: string;
    application_id: string;
    offer_id: number;
    amount: string;
    loan_id?: string | null;
    user_id: number;
    username?: string;
    /** true when `force` replaced an existing offer. */
    regenerated?: boolean;
    created_at?: string | null;
    data?: unknown;
    error?: string;
}

/** Response of `caprionUploadDocument`. */
export interface CaprionUploadDocumentResponse {
    success: boolean;
    message: string;
    code?: number | null;
    data?: unknown;
    application_id: string;
    applicant_id: string;
    /** true when the backend derived applicant_id instead of being given one. */
    applicant_id_resolved: boolean;
    files_sent: string[];
    error?: string;
}

/** Response of `caprionCheckStatusResponse` — one call's full stored body. */
export interface CaprionCheckStatusResponseDetail {
    id: number;
    status_type: string | null;
    success: boolean | null;
    status_code: number | null;
    is_final_response: boolean | null;
    application_id: string | null;
    applicant_id: string | null;
    created_at: string | null;
    response: unknown;
}

/**
 * One failed Caprion API call — the "Page 11" section of View Failures.
 *
 * `request_json` / `response_json` are the verbatim stored documents, typed as
 * `unknown` because they are arbitrary lender payloads whose shape varies per
 * api_name. The UI pretty-prints them rather than reading named fields.
 */
export interface CaprionApiFailureRow {
    id: number;
    api_name: string | null;
    api_url: string | null;
    http_method: string | null;
    application_id: string | null;
    applicant_id: string | null;
    status_code: number | null;
    status_type: string | null;
    error_message: string | null;
    user_id: number | null;
    username: string | null;
    created_at: string | null;
    request_json: unknown;
    response_json: unknown;
    /** True for save_aadhaar_details — rendered prominently, others muted. */
    is_priority: boolean;
}

/** One failed verification — the "Page 15" section of View Failures. */
export interface VerificationFailureRow {
    id: number;
    status: string | null;
    /** WHAT specifically failed. */
    verification_code: string | null;
    verification_name: string | null;
    /** The message / remark. message_to_show is borrower-facing, comment internal. */
    message_to_show: string | null;
    comment: string | null;
    need_manual_review: boolean | null;
    reason_for_manual_review: string | null;
    manual_review_done: boolean | null;
    user_id: number | null;
    username: string | null;
    created_at: string | null;
    updated_at: string | null;
}

/** One labelled section of the View Failures response. */
export interface FailureSection<TRow> {
    label: string;
    title: string;
    total: number;
    count: number;
    total_pages: number;
    results: TRow[];
    priority_api_name?: string;
}

/**
 * Response of `caprionViewFailures`.
 *
 * `page_11` / `page_15` are the operator-facing LABELS for the two feeds, not a
 * filter on journey position — see the endpoint docstring in admin_views.py.
 */
export interface CaprionViewFailuresResponse {
    page: number;
    page_size: number;
    max_page_size: number;
    page_11: FailureSection<CaprionApiFailureRow>;
    page_15: FailureSection<VerificationFailureRow>;
    filters: { mobile?: string };
    /** True when no mobile number was supplied — sections are empty by design. */
    needs_input?: boolean;
    message?: string;
    /** How many auth_user rows the number matched (includes archived accounts). */
    matched_user_count?: number;
}

/**
 * One user's row. The scalar fields are fixed; `check_status_1..N` arrive as
 * extra keys, hence the index signature.
 */
export interface CaprionJourneyRow {
    user_id: number;
    name: string | null;
    phone_number: string | null;
    application_id: string | null;
    applicant_id: string | null;
    order_id: string | null;
    final_status: string | null;
    final_lender_status: string | null;
    current_page_number: number | null;
    current_page_name: string | null;
    current_page_slug: string | null;
    source_slug: string | null;
    business_name: string | null;
    first_called_at: string | null;
    last_called_at: string | null;
    [key: string]: unknown;
}

export interface CaprionJourneyStatusResponse {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    count: number;
    results: CaprionJourneyRow[];
    columns: CaprionJourneyColumn[];
    choices: {
        final_status: string[];
        source_slug: string[];
        page_name: string[];
    };
    filters: Record<string, string>;
    max_page_size: number;
}

export interface CaprionJourneyStatusParams {
    page?: number;
    page_size?: number;
    search?: string;
    final_status?: string;
    source_slug?: string;
    page_name?: string;
    created_from?: string;
    created_to?: string;
}

export interface BasicLoanDetailsUploadResponse {
    status: string;
    message: string;
}

export interface BasicLoanUploadStatus {
    csv_last_uploaded_at: string | null;
    last_task_status: 'STARTED' | 'SUCCESS' | 'FAILURE' | null;
    last_result: {
        rows_added: number;
        invalid_loan_indexes: number[];
        invalid_user_indexes: number[];
        invalid_row_indexes: number[];
        error?: string;
    } | null;
}

/**
 * Response of BOTH `lookupUserProfile` (GET) and `updateUserProfile` (POST) —
 * the backend deliberately returns the same shape from both so the "Edit User"
 * dialog can re-seed its form straight from the save response and never has to
 * re-fetch to show the committed state.
 *
 * `salutation_choices` / `gender_choices` come from the IndividualDetail model's
 * own `choices`, so the dropdowns cannot drift out of sync with the column.
 */
export interface AdminUserProfile {
    user_id: number;
    /** == the mobile number; auth_user.username IS the phone everywhere here. */
    username: string;
    email: string;
    is_active: boolean;
    first_name: string;
    last_name: string;
    /** null = the borrower has no IndividualDetail row yet (save creates one). */
    individual_detail_id: number | null;
    salutation: string;
    /** ISO `YYYY-MM-DD`, or "" when unset — feeds <input type="date"> directly. */
    date_of_birth: string;
    gender: string;
    /** Read-only confirmation context; null when the borrower has no business row. */
    business: {
        business_detail_id: number;
        business_name: string;
        business_constitution_type: string;
        company_pan_number: string;
        gst_number: string;
    } | null;
    salutation_choices: string[];
    gender_choices: string[];
    /** Update response only: the "<table>.<field>" writes that actually changed. */
    updated?: string[];
    detail?: string;
}

/** Only the fields the Edit User dialog is allowed to write. */
export interface AdminUserProfileUpdate {
    first_name?: string;
    last_name?: string;
    salutation?: string;
    date_of_birth?: string;
    gender?: string;
}

/**
 * Response of `clearJourneyMeta` — the "Unblock Verification" tool.
 *
 * `was_blocked_by` lists which of the two block flags were actually set, so the
 * dialog can distinguish "this user really was blocked" from "meta had no block
 * flags at all" instead of showing a misleading success message.
 */
export interface AdminClearJourneyMetaResponse {
    detail: string;
    /** false on a dry run. */
    cleared: boolean;
    user_id: number;
    username: string;
    current_stage: string;
    /** e.g. ["bank_verification_blocked"]; empty if the user was not blocked. */
    was_blocked_by: string[];
    previous_meta: Record<string, unknown>;
    new_meta: Record<string, unknown>;
}

export const adminService = {
    getUsers: () => axios.get(`${API_BASE_URL}/api/public/users/`),
    createUser: (data: any) => axios.post(`${API_BASE_URL}/api/public/users/create/`, data),
    updateUser: (data: any) => axios.post(`${API_BASE_URL}/api/public/users/update/`, data),
    getReferenceData: () => axios.get(`${API_BASE_URL}/api/admin/reference-data/`),
    getRoles: () => axios.get(`${API_BASE_URL}/api/public/roles/`),
    createRole: (data: any) => axios.post(`${API_BASE_URL}/api/public/roles/create/`, data),
    updateRole: (data: any) => axios.post(`${API_BASE_URL}/api/public/roles/update/`, data),

    // Pre-Approved Leads
    listPreApproved: (params?: { page?: number; page_size?: number; search?: string }) =>
        axios.get(`${API_BASE_URL}/api/admin/preapproved/`, { params }),
    addPreApproved: (entries: { restaurant_id?: number | null; phone_number: string; restaurant_name?: string | null }[]) =>
        axios.post(`${API_BASE_URL}/api/admin/preapproved/add/`, { entries }),
    addPreApprovedCsv: (file: File) => {
        const form = new FormData();
        form.append('csv_file', file);
        return axios.post(`${API_BASE_URL}/api/admin/preapproved/add/`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    editPreApproved: (pk: number, data: { restaurant_id?: number | null; phone_number?: string; restaurant_name?: string | null }) =>
        axios.patch(`${API_BASE_URL}/api/admin/preapproved/${pk}/edit/`, data),
    deletePreApproved: (pk: number) => axios.delete(`${API_BASE_URL}/api/admin/preapproved/${pk}/delete/`),

    // Whitelist Pincodes (Caprion location serviceability)
    listWhitelistPincode: (params?: { page?: number; page_size?: number; search?: string }) =>
        axios.get(`${API_BASE_URL}/api/admin/whitelist-pincode/`, { params }),
    addWhitelistPincode: (entries: string[]) =>
        axios.post(`${API_BASE_URL}/api/admin/whitelist-pincode/add/`, { entries }),
    addWhitelistPincodeCsv: (file: File) => {
        const form = new FormData();
        form.append('csv_file', file);
        return axios.post(`${API_BASE_URL}/api/admin/whitelist-pincode/add/`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    deleteWhitelistPincode: (pk: number) =>
        axios.delete(`${API_BASE_URL}/api/admin/whitelist-pincode/${pk}/delete/`),

    // Delete User Journey
    deleteUserJourney: (user_id: number, phone_number: string) => axios.post(`${API_BASE_URL}/api/admin/delete_user_journey/`, { user_id, phone_number }),

    // Archive User — frees the phone number (username rename + is_active=false)
    // WITHOUT deleting any row. Both user_id and username must match the same
    // auth_user row. Pass dry_run to preview before applying.
    archiveUser: (user_id: number, username: string, dry_run = false) =>
        axios.post(`${API_BASE_URL}/api/admin/archive_user/`, { user_id, username, dry_run }),

    // Edit User — look up a borrower by mobile number (which IS their
    // auth_user.username) to autofill the dialog. Read-only.
    lookupUserProfile: (username: string) =>
        axios.get<AdminUserProfile>(`${API_BASE_URL}/api/admin/user_profile/`, { params: { username } }),

    // Edit User — save. Only the keys present in `changes` are written, so
    // omitting a field leaves it untouched rather than clearing it.
    updateUserProfile: (username: string, changes: AdminUserProfileUpdate) =>
        axios.post<AdminUserProfile>(`${API_BASE_URL}/api/admin/user_profile/update/`, { username, ...changes }),

    // Unblock Verification — resets LoanJourneyState.meta to {} so the auto-fire
    // verification pages re-run their checks instead of short-circuiting on the
    // persisted block flag. Pass dry_run to see the current meta first.
    clearJourneyMeta: (username: string, dry_run = false) =>
        axios.post<AdminClearJourneyMetaResponse>(`${API_BASE_URL}/api/admin/clear_journey_meta/`, { username, dry_run }),

    // Reset Journey — list the user's pages, numbered 1..N for the dropdown
    // (page 1 == checkpoint_index 0). Read from the user's own
    // checkpoint_template, so it reflects their actual flow.
    // Identified by mobile number (which IS the username) or by user_id.
    getJourneyPages: (identifier: { username?: string; user_id?: number }) =>
        axios.get(`${API_BASE_URL}/api/admin/journey_pages/`, { params: identifier }),

    // Reset Journey — move the user to a page number (1-based).
    resetJourneyToPage: (
        identifier: { username?: string; user_id?: number },
        page_number: number,
        dry_run = false,
    ) =>
        axios.post(`${API_BASE_URL}/api/admin/reset_journey_to_page/`, { ...identifier, page_number, dry_run }),

    // Petpooja Dump — submit raw webhook payload via admin
    petpoojaDump: (payload: object) => axios.post(`${API_BASE_URL}/api/admin/petpooja_dump/`, payload, {
        headers: { 'Content-Type': 'application/json' },
    }),

    // Petpooja Dump — list existing dump records
    listPetpoojaDumps: (params?: { page?: number; page_size?: number; search?: string }) =>
        axios.get(`${API_BASE_URL}/api/admin/petpooja_dump/list/`, { params }),

    // Journey State — get LoanJourneyState for a user
    getJourneyState: (userId: number) =>
        axios.get(`${API_BASE_URL}/api/admin/journey_state/${userId}/`),

    // Journey State — update LoanJourneyState for a user
    updateJourneyState: (userId: number, data: {
        current_stage?: string;
        last_route?: string;
        meta?: Record<string, any>;
        checkpoint_template?: any[][];
        current_checkpoint_index?: number;
        is_locked?: boolean;
    }) =>
        axios.patch(`${API_BASE_URL}/api/admin/journey_state/${userId}/update/`, data),

    // Journey Presets — list all active LenderBAPJourney records
    listJourneyPresets: () =>
        axios.get(`${API_BASE_URL}/api/admin/journey_presets/`),

    // Marketing Links — list PetpoojaMarketingLink records
    listMarketingLinks: (params?: { page?: number; page_size?: number; search?: string }) =>
        axios.get(`${API_BASE_URL}/api/admin/marketing_links/`, { params }),

    // Fixer: Check DigiLocker Status
    fixerCheckDigilockerStatus: (requestId: number) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/check_digilocker_status/`, { request_id: requestId }),

    // Fixer: Fetch Digio Documents
    fixerFetchDigioDocuments: (params: {
        user_id: number;
        request_id?: string | null;
        lender_name?: string | null;
        provider_name?: string | null;
        dry_run?: boolean;
    }) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/fetch_digio_documents/`, params),

    // Fixer: KYC Backfill
    fixerKycBackfill: (params: {
        request_id: string;
        user_id: number;
        lender_name?: string;
        provider_name?: string;
        adhar_file_url?: string;
        pan_file_url?: string;
        adhar_xml?: string;
        pan_xml?: string;
        adhar_photo_base64?: string;
        dry_run?: boolean;
    }) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/kyc_backfill/`, params),

    // Fixer: Run Verifications
    fixerRunVerifications: (params: {
        user_id: number;
        dry_run?: boolean;
        codes?: string[] | null;
        pending_only?: boolean;
    }) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/run_verifications/`, params),

    // Fixer: Retrigger Selfie/Aadhaar Match — @origin: v2 | @since: 6d0c364b
    fixerRetriggerSelfieMatch: (params: { user_id: number; minimum_match?: number }) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/retrigger_selfie_match/`, params),

    // @origin: v2 | @since: 3d9ebf6d, c064a0da | @used-by: ALL | @purpose: 2f
    // port -- Stage Analysis / URL-Time / Page-Time admin analytics panels.
    // POST (not GET) so filter values travel in the request body.

    // Stage Analysis — per-JourneyStage aggregate metrics
    getStageAnalysis: (params?: { bap_id?: string; le_id?: string; exclude_staff_like?: boolean }) =>
        axios.post(`${API_BASE_URL}/api/admin/stage_analysis/`, params || {}),

    // Per-user chronological JourneyEvent history (for URL/Time analysis page)
    getUserJourneyEvents: (userId: number) =>
        axios.post(`${API_BASE_URL}/api/admin/user_journey_events/${userId}/`, {}),

    // Page (route) time analytics — global + optional per-user drill-down
    getPageTimeAnalytics: (params?: { user_id?: number; exclude_staff_like?: boolean }) =>
        axios.post(`${API_BASE_URL}/api/admin/page_time_analytics/`, params || {}),

    /**
     * Expenses: Digitap API logs — paginated list + aggregates + filter choices.
     *
     * SUPERUSER ONLY. A non-superuser receives a 403 JSON body (deliberately
     * NOT a 302 to the login page, which the frontend could not distinguish
     * from success), surfaced by ExpensesPanel as an explicit message.
     *
     * Pass `include_payloads: 1` ONLY for a narrowly-scoped, single-row lookup:
     * the payload blobs contain raw mobile numbers and PANs.
     * @origin: v2 | @since: 0570ba2d | @used-by: ALL
     */
    listDigitapLogs: (
        params?: DigitapLogsParams,
        options?: { signal?: AbortSignal },
    ): Promise<AxiosResponse<DigitapLogsResponse>> =>
        axios.get<DigitapLogsResponse>(`${API_BASE_URL}/api/admin/digitap_logs/`, {
            params,
            signal: options?.signal,
        }),

    // Caprion: Fetch Repayment Schedule
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    caprionFetchRps: (application_id: string) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/fetch_rps/`, { application_id }),

    // Caprion: Check Application Status — pass either application_id or urn (not both)
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    caprionCheckStatus: (params: { application_id?: string; urn?: string }) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/check_status/`, params),

    // Address Match Report — every address we hold for one borrower, scored
    // against the authoritative sources (Aadhaar / PAN / Udyam / Digitap /
    // selfie GPS). READ-ONLY: reads our own tables plus one Google Geocoding
    // call and never touches the lender, so unlike caprionCheckStatus above
    // this is free to re-run as often as you like.
    // Response: { success, report: {...}, text: "copy-paste block" }
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    addressMatchReport: (params: { mobile: string }) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/address_match_report/`, params),

    // Address CPV Range Report — the bulk form of addressMatchReport above.
    //
    // Three calls instead of one because the per-borrower scoring is far too
    // slow to run a range inside a single request (it would 504): `start`
    // dispatches a Celery task, `progress` polls it, `download` serves the
    // xlsx once it is done.
    //
    // `start`/`end` are 1-BASED INCLUSIVE POSITIONS in the borrower list
    // ordered by id, NOT user ids. The backend clamps a range that runs past
    // the end of the table, so an over-long range returns what exists rather
    // than erroring.
    // Pass EITHER `mobiles` (comma/space/newline separated, or an array) OR
    // `start`+`end`. When both are present the backend uses `mobiles`.
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    addressCpvRangeStart: (
        params:
            | { mobiles: string | string[] }
            | { start: number; end: number },
    ) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/address_cpv_range/start/`, params),

    // Poll a dispatched range report.
    // Response: { state, done, total, columns?, rows?, failed?, error? }
    // where state is PENDING | STARTED | PROGRESS | SUCCESS | FAILURE.
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    addressCpvRangeProgress: (task_id: string) =>
        axios.get(`${API_BASE_URL}/api/admin/caprion/address_cpv_range/progress/`, {
            params: { task_id },
        }),

    // Download the finished range report as .xlsx.
    //
    // `responseType: 'blob'` is REQUIRED — without it axios would try to parse
    // the binary xlsx body as text and corrupt the file.
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    addressCpvRangeDownload: (task_id: string) =>
        axios.get(`${API_BASE_URL}/api/admin/caprion/address_cpv_range/download/`, {
            params: { task_id },
            responseType: 'blob',
        }),

    // Caprion: Create Non-Captive Lead — forward raw payload to Caprion CreateLead API
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    caprionCreateLead: (payload: Record<string, any>) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/create_lead/`, payload),

    // Caprion: Cancel Lead — cancel/withdraw a Caprion loan application
    // @origin: v3 | @since: native | @used-by: LND:CAPRION
    caprionCancelLead: (params: { application_id: string; applicant_id: string; remarks: string; timestamp?: string }) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/cancel_lead/`, params),

    /**
     * Caprion: Generate Initial Offer — create the initial BRELoanOffers row
     * for an application if one does not already exist.
     *
     * Only `application_id` is needed: the backend resolves the borrower from
     * the application's BRE2 approval (an admin has no borrower session) and
     * reuses the same offer maths the journey itself runs.
     *
     * Idempotent — if an offer already exists the call is a no-op and returns
     * `created: false`. Pass `force` only to deliberately regenerate.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionGenerateInitialOffer: (application_id: string, force = false) =>
        axios.post<CaprionGenerateInitialOfferResponse>(
            `${API_BASE_URL}/api/admin/caprion/generate_initial_offer/`,
            { application_id, force },
        ),

    /**
     * Caprion: Upload Document — push one or more files to Caprion's
     * UploadDocument API for an existing application.
     *
     * Only `application_id` is required from the operator: the backend resolves
     * applicant_id from the CaprionRequestResponse audit trail (1:1 mapping), so
     * a document can never be filed against the wrong borrower by a typo.
     *
     * Files are appended under the repeated `files[]` field name, which is what
     * Caprion itself expects. Content-Type is left to the browser so it sets the
     * multipart boundary — setting it by hand here breaks the upload.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionUploadDocument: (params: {
        application_id: string;
        document_type: string;
        files: File[];
        remarks?: string;
        /** Optional override; normally resolved server-side. */
        applicant_id?: string;
    }) => {
        const form = new FormData();
        form.append('application_id', params.application_id);
        form.append('document_type', params.document_type);
        if (params.remarks) form.append('remarks', params.remarks);
        if (params.applicant_id) form.append('applicant_id', params.applicant_id);
        params.files.forEach((file) => form.append('files[]', file));
        return axios.post<CaprionUploadDocumentResponse>(
            `${API_BASE_URL}/api/admin/caprion/upload_document_pushload/`,
            form,
        );
    },

    /**
     * Caprion: Manage Mandates list — every not-yet-verified mandate for the
     * PhonePe BAP, so an admin can re-poll them one at a time.
     *
     * "Not yet verified" = status waiting / processing / unknown / unset. Rows with
     * status=accepted (already verified) and status=rejected (resolved outcome) are
     * excluded — which is why a row DISAPPEARS from this list once its verify call
     * succeeds and writes status=accepted.
     *
     * READ-ONLY and free: pure DB read, no Digio call, so refetching after each
     * verify costs nothing.
     *
     * SUPERUSER ONLY — a non-superuser receives a 403 JSON body.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION, BAP:PHONEPE
     */
    caprionPendingMandates: (
        params?: { limit?: number },
    ): Promise<AxiosResponse<CaprionPendingMandatesResponse>> =>
        axios.get<CaprionPendingMandatesResponse>(
            `${API_BASE_URL}/api/admin/caprion/pending_mandates/`,
            { params },
        ),

    /**
     * Caprion: Trigger Verify Mandate — manually re-run the Digio mandate status
     * check for one stuck mandate and persist the result so the borrower's
     * eMandate page renders it as verified.
     *
     * `mandate_id` is UserMandateRegistrationMapping.mandate_id — the Digio
     * mandate id, which is the same value Caprion calls merchant_request_number.
     * It is the lookup key because we do NOT hold the UMRN until NPCI confirms
     * the mandate — `umrn` stays NULL on the row until a successful status poll
     * fills it in.
     *
     * mandate_id is unique, so the owner and the UPI-vs-NACH type are both derived
     * from the row server-side — neither is sent.
     *
     * `lender_name` is optional and defaults to the lender resolved from the
     * user's journey; it only needs supplying when that resolution has drifted,
     * since the lender decides which Digio credentials are used.
     *
     * NOT FREE and NOT idempotent-cheap: this hits Digio's live API, so it is an
     * on-demand button, never a poll.
     *
     * SUPERUSER ONLY — a non-superuser receives a 403 JSON body.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionVerifyMandate: (params: {
        mandate_id: string;
        lender_name?: string;
    }) =>
        axios.post(`${API_BASE_URL}/api/admin/caprion/verify_mandate/`, params),

    /**
     * Caprion Journey Status — one row per user with application_id, derived
     * final_status, every check_status response body, the journey page they are
     * on and their source_slug.
     *
     * READ-ONLY and free: the backend reads the persisted
     * CaprionRequestResponse audit trail and never calls Caprion's live API, so
     * polling this costs nothing (unlike caprionCheckStatus above, which DOES
     * hit the lender).
     *
     * SUPERUSER ONLY — a non-superuser receives a 403 JSON body.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionJourneyStatus: (
        params?: CaprionJourneyStatusParams,
        options?: { signal?: AbortSignal },
    ): Promise<AxiosResponse<CaprionJourneyStatusResponse>> =>
        axios.get<CaprionJourneyStatusResponse>(
            `${API_BASE_URL}/api/admin/caprion/journey_status/`,
            { params, signal: options?.signal },
        ),

    /**
     * Fetch ONE check_status call's verbatim stored response body.
     *
     * The list endpoint above deliberately omits those bodies (~2.7 KB each —
     * shipping them inline is what made BRE Analysis slow), so the JSON viewer
     * loads the one it is about to display. Read-only: this reads the persisted
     * audit row and never calls Caprion's live API.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionCheckStatusResponse: (
        id: number,
        options?: { signal?: AbortSignal },
    ): Promise<AxiosResponse<CaprionCheckStatusResponseDetail>> =>
        axios.get<CaprionCheckStatusResponseDetail>(
            `${API_BASE_URL}/api/admin/caprion/check_status_response/`,
            { params: { id }, signal: options?.signal },
        ),

    /**
     * View Failures — one borrower's two failure feeds, for the Caprion Tools
     * dialog.
     *
     * `mobile` is REQUIRED: this is a per-customer lookup, so calling it
     * without a number returns empty sections and `needs_input: True` rather
     * than every failure in the system.
     *
     * Returns BOTH sections in one call:
     *   page_11 — that user's failed Caprion API calls (success=False), with
     *             save_aadhaar_details ranked first and each row carrying its
     *             verbatim request_json + response_json.
     *   page_15 — that user's failed verifications, naming the specific check
     *             plus its message / remark.
     *
     * READ-ONLY and free: the backend reads persisted audit/tracker rows and
     * never calls Caprion's live API, so refreshing costs nothing.
     *
     * SUPERUSER ONLY — a non-superuser receives a 403 JSON body.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionViewFailures: (
        params?: { mobile?: string; page?: number; page_size?: number },
        options?: { signal?: AbortSignal },
    ): Promise<AxiosResponse<CaprionViewFailuresResponse>> =>
        axios.get<CaprionViewFailuresResponse>(
            `${API_BASE_URL}/api/admin/caprion/view_failures/`,
            { params, signal: options?.signal },
        ),

    /**
     * Caprion Journey Status CSV export.
     *
     * Returns a Blob, so the caller must trigger the download itself (see
     * CaprionJourneyStatus.tsx). `fields` is a comma-separated list of column
     * keys in the desired order — omit it for every column. The export is NOT
     * paginated: it always covers the whole filtered set, not just the page on
     * screen.
     * @origin: v3 | @since: native | @used-by: LND:CAPRION
     */
    caprionJourneyStatusCsv: (
        params?: CaprionJourneyStatusParams & { fields?: string },
    ): Promise<AxiosResponse<Blob>> =>
        axios.get(`${API_BASE_URL}/api/admin/caprion/journey_status/csv/`, {
            params,
            responseType: 'blob',
        }),


    uploadBasicLoanDetails: (file: File): Promise<AxiosResponse<BasicLoanDetailsUploadResponse>> => {
        const form = new FormData();
        form.append("basic_loan_details", file);
        return axios.post(`${API_BASE_URL}/api/admin/basic_loan_details/upload_pushload`, form);
    },

    getBasicLoanUploadStatus: (): Promise<AxiosResponse<BasicLoanUploadStatus>> =>
        axios.get(`${API_BASE_URL}/api/admin/basic_loan_details/status/`),
    // Fixer: Check Offer / Failure — fetch all PPBRERawResponse records for a restaurant_id, optionally filtered by user_id
    fixerCheckOfferFailure: (restaurant_id: number, userId?: number) =>
        axios.get(`${API_BASE_URL}/api/admin/fixer/check_offer_failure/`, {
            params: userId != null ? { restaurant_id, user_id: userId } : { restaurant_id },
        }),

    // Fixer: Lender Rejections — fetch BRE rules analysis for a restaurant's loan applications
    fixerListDigilockerTrackers: (user_id: number) =>
        axios.get(`${API_BASE_URL}/api/admin/fixer/digilocker_trackers/`, { params: { user_id } }),

    fixerLenderRejections: (restaurant_id: number, userId?: number) =>
        axios.get(`${API_BASE_URL}/api/admin/fixer/lender_rejections/`, {
            params: userId != null ? { restaurant_id, user_id: userId } : { restaurant_id },
        }),

    // Info: Banking & Mandates — paginated list of mandate rows + latest BankDetail per user
    infoBankingMandates: (params?: { page?: number; page_size?: number; user_id?: number }) =>
        axios.get(`${API_BASE_URL}/api/admin/info/banking_mandates/`, { params }),

    // Info: All BankDetail rows for a single user (per-row expansion)
    infoAllBankDetails: (userId: number) =>
        axios.get(`${API_BASE_URL}/api/admin/info/banking_mandates/`, {
            params: { all_bank_details: 1, user_id: userId },
        }),

    // Fixer: Fetch & Update Mandate Status — polls Digio live for all mandates of a user
    // Table: primary_app_usermandateregistrationmapping (status, response, updated_at)
    fixerMandateStatus: (params: { user_id: number; dry_run?: boolean }) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/mandate_status/`, params),

    // Fixer: Doc Push Preview — build the full payload without pushing (saves to doc_push_raw_request)
    // Returns { ok: true, payload: { ... } }
    fixerDocPushPreview: (user_id: number) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/doc_push_preview/`, { user_id }),

    // Fixer: Doc Push — execute push_loan_documents as the target user (admin-proxied)
    // Writes doc_push_raw_request, doc_push_response, application_status to LoanApplicationMapping
    fixerDocPush: (user_id: number) =>
        axios.post(`${API_BASE_URL}/api/admin/fixer/doc_push/`, { user_id }),

    // Fixer: Check Doc Push Status — poll async push_loan_documents_task progress for a user
    // Returns { status: 'DOCS_PUSHED' | 'PUSH_FAILED' | 'DOC_PUSH_REJECTED' | 'PUSHING' | ... }
    fixerCheckDocPushStatus: (user_id: number) =>
        axios.get(`${API_BASE_URL}/api/admin/fixer/doc_push_status/`, { params: { user_id } }),

    // Expenses: Digitap API Logs — paginated list with filters
    listDigitapLogs: (params?: {
        page?: number;
        page_size?: number;
        api_type?: string;
        outcome?: string;
        user_id?: string;
        search?: string;
    }) => axios.get(`${API_BASE_URL}/api/admin/digitap_logs/`, { params }),

    // BRE Injector: fetch latest mapping info for a user (pre-populates the form)
    adminGetMappingInfo: (userId: number) =>
        axios.get(`${API_BASE_URL}/api/admin/mapping_info/`, { params: { user_id: userId } }),

    // Testing: Name Lookup — fetch Digio PAN name + Digitap PAN name for a user
    testingNameLookup: (userId: number) =>
        axios.get(`${API_BASE_URL}/api/admin/testing/name_lookup/`, { params: { user_id: userId } }),

    // Testing: PAN Lookup — fetch Digio PAN name + Digitap PAN name for a raw PAN string
    testingPanLookup: (pan: string) =>
        axios.get(`${API_BASE_URL}/api/admin/testing/pan_lookup/`, { params: { pan } }),

    // Testing: Name Match — compare two names using name_match_score_old
    testingNameMatch: (params: { name1: string; name2: string; threshold?: number }) =>
        axios.post(`${API_BASE_URL}/api/admin/testing/name_match/`, params),

    // Testing: Trigger Disbursement — fire DisbursementAPIView with a dummy or custom payload
    // loan_id must match an existing LoanApplicationMapping.loan_reference_number
    // payload is optional; if omitted the backend builds a minimal 3-instalment dummy
    testingTriggerDisbursement: (params: { loan_id: string; payload?: Record<string, any> }) =>
        axios.post(`${API_BASE_URL}/api/admin/testing/trigger_disbursement/`, params),

    // BRE Injector: inject a BRE request + response and process it exactly as
    // the async evaluate_lender_bre flow does — without making an external HTTP call.
    injectBREResponse: (params: {
        user_id: number;
        mapping_id?: number | null;
        lender_name: string;
        offer_id?: number | null;
        bre_request: Record<string, any>;
        bre_response: Record<string, any>;
    }) => axios.post(`${API_BASE_URL}/api/admin/inject_bre_response/`, params),

    // Address Verify: geocode user-entered address and compare distance to GPS-detected coords
    verifyAddress: (params: {
        address: {
            room_no: string; building_name: string; society_name: string;
            street_name: string; area: string; landmark: string;
            pincode: string; city: string; state: string;
        };
        detected_lat: number | null;
        detected_lng: number | null;
        detected_address: string;
    }) => axios.post(`${API_BASE_URL}/api/admin/testing/address_verify/`, params),
};
